import React, { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { useProfile } from '@/hooks/useProfile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  CheckSquare, 
  FileText, 
  AlertTriangle, 
  Building2, 
  Activity,
  Users,
  Calendar,
  TrendingUp,
  CheckCircle,
  Clock,
  Gavel,
  Sparkles,
  Crown,
  Zap,
  LogOut,
  Settings
} from 'lucide-react';
import ComplianceScoreCard from '@/components/ComplianceScoreCard';
import EmergencyActionsCard from '@/components/EmergencyActionsCard';
import MobileAppCard from '@/components/dashboard/MobileAppCard';
import TrialBanner from '@/components/billing/TrialBanner';
import StarterPackCard from '@/components/dashboard/StarterPackCard';


interface DashboardStats {
  pendingChecklists: number;
  completedToday: number;
  openIncidents: number;
  totalUnits: number;
}

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { hasCompletedSetup, isLoading: preferencesLoading } = useUserPreferences();
  const [stats, setStats] = useState<DashboardStats>({
    pendingChecklists: 0,
    completedToday: 0,
    openIncidents: 0,
    totalUnits: 0
  });
  const [userRole, setUserRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [hasOnboarded, setHasOnboarded] = useState(false);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;

      try {
        // Check if user has been onboarded (has a tenant role)
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role, tenant_id')
          .eq('user_id', user.id)
          .single();

        if (!roleData) {
          // User hasn't been onboarded, redirect to onboarding
          navigate('/onboarding');
          return;
        }

        setUserRole(roleData.role);
        setTenantId(roleData.tenant_id);
        setHasOnboarded(true);

        // Fetch real dashboard statistics
        await fetchDashboardStats(roleData.tenant_id);
        await fetchRecentActivity(roleData.tenant_id);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardStats = async (tenantId: string) => {
    try {
      // Get pending checklists count
      const { count: pendingCount } = await supabase
        .from('checklists')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // Get completed today count
      const today = new Date().toISOString().split('T')[0];
      const { count: completedCount } = await supabase
        .from('checklists')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed')
        .gte('completed_at', `${today}T00:00:00.000Z`)
        .lte('completed_at', `${today}T23:59:59.999Z`);

      // Get open incidents count
      const { count: incidentsCount } = await supabase
        .from('incidents')
        .select('*', { count: 'exact', head: true })
        .is('resolved_at', null);

      // Get total units count
      const { count: unitsCount } = await supabase
        .from('units')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId);

      setStats({
        pendingChecklists: pendingCount || 0,
        completedToday: completedCount || 0,
        openIncidents: incidentsCount || 0,
        totalUnits: unitsCount || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Keep default values on error
    }
  };

  const fetchRecentActivity = async (tenantId: string) => {
    try {
      const { data: activities } = await supabase
        .from('audit_events')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('timestamp', { ascending: false })
        .limit(5);

      setRecentActivity(activities || []);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setRecentActivity([]);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading || !hasOnboarded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {loading ? 'Loading...' : 'Setting up your account...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <main className="flex-1 bg-gradient-to-br from-background to-accent/20">
          <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Crystal HACCP Dashboard
                </h1>
                <p className="text-muted-foreground mt-1">
                  Welcome back, {profile?.name || user?.email}!
                </p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={profile?.avatar_url || ''} alt={profile?.name || ''} />
                      <AvatarFallback>{getInitials(profile?.name || '')}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{profile?.name || 'User'}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {profile?.email || user?.email}
                      </p>
                      <Badge variant="outline" className="mt-2 w-fit">
                        {userRole}
                      </Badge>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>My Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/system-preferences')}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <TrialBanner />

            <StarterPackCard tenantId={tenantId} />

            {/* Next Best Action banner */}

            {(() => {
              const action = !hasCompletedSetup
                ? { title: 'Finish your setup', desc: 'Configure regions, law packs and your first unit.', cta: 'Open Quick Setup', to: '/quick-setup', icon: Zap, tone: 'text-primary bg-primary/10' }
                : stats.openIncidents > 0
                ? { title: `${stats.openIncidents} open incident${stats.openIncidents > 1 ? 's' : ''} need attention`, desc: 'Resolve or assign a corrective action to stay audit-ready.', cta: 'Review Incidents', to: '/incidents', icon: AlertTriangle, tone: 'text-red-500 bg-red-500/10' }
                : stats.pendingChecklists > 0
                ? { title: `${stats.pendingChecklists} checklist${stats.pendingChecklists > 1 ? 's' : ''} pending today`, desc: 'Run today\'s scheduled checks before the end of shift.', cta: 'Run Checklists', to: '/checklists', icon: CheckSquare, tone: 'text-orange-500 bg-orange-500/10' }
                : { title: 'You\'re all caught up', desc: 'No outstanding actions. Great time to review reports or invite a teammate.', cta: 'View Reports', to: '/reports', icon: CheckCircle, tone: 'text-green-500 bg-green-500/10' };
              const Icon = action.icon;
              return (
                <Card className="glass-panel-elevated p-4 mb-6 flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${action.tone}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-tight">{action.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{action.desc}</p>
                  </div>
                  <Button size="sm" onClick={() => navigate(action.to)} className="shrink-0">
                    {action.cta}
                  </Button>
                </Card>
              );
            })()}

            {/* Compliance Hero Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2">
                <ComplianceScoreCard 
                  score={96}
                  nextInspectionDays={45}
                  criticalActions={stats.openIncidents}
                />
              </div>
              <div>
                <EmergencyActionsCard />
              </div>
            </div>

            {/* Mobile app */}
            <MobileAppCard />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="glass-panel">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Checklists</CardTitle>
                  <Clock className="h-4 w-4 text-orange-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.pendingChecklists}</div>
                  <p className="text-xs text-muted-foreground">Require attention</p>
                </CardContent>
              </Card>

              <Card className="glass-panel">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Completed Today</CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.completedToday}</div>
                  <p className="text-xs text-muted-foreground">On schedule</p>
                </CardContent>
              </Card>

              <Card className="glass-panel">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Open Incidents</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.openIncidents}</div>
                  <p className="text-xs text-muted-foreground">Need action</p>
                </CardContent>
              </Card>

              <Card className="glass-panel">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Units</CardTitle>
                  <Building2 className="h-4 w-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalUnits}</div>
                  <p className="text-xs text-muted-foreground">Active locations</p>
                </CardContent>
              </Card>
            </div>

            {/* Setup Completion Card - Only show if setup not completed */}
            {!hasCompletedSetup && (
              <Card className="glass-panel-elevated p-6 border-primary/50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">Complete Your Setup</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Configure your system settings, select law packs, and explore our Crystal HACCP templates to get started.
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => navigate('/quick-setup')} className="flex-1">
                        Setup Wizard
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate('/crystal-templates')}
                        className="flex-1"
                      >
                        Browse Templates
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card 
                className="glass-panel hover:glass-panel-elevated transition-all duration-300 cursor-pointer group"
                onClick={() => navigate('/checklists')}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <CheckSquare className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Run Checklist</h3>
                  <p className="text-sm text-muted-foreground mb-4">Start a new checklist inspection</p>
                  <Button size="sm" className="w-full">Start Checklist</Button>
                </CardContent>
              </Card>

              <Card 
                className="glass-panel hover:glass-panel-elevated transition-all duration-300 cursor-pointer group"
                onClick={() => navigate('/reports')}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary/20 transition-colors">
                    <FileText className="w-6 h-6 text-secondary" />
                  </div>
                  <h3 className="font-semibold mb-2">View Reports</h3>
                  <p className="text-sm text-muted-foreground mb-4">Generate compliance reports</p>
                  <Button variant="outline" size="sm" className="w-full">View Reports</Button>
                </CardContent>
              </Card>

              <Card 
                className="glass-panel hover:glass-panel-elevated transition-all duration-300 cursor-pointer group"
                onClick={() => navigate('/law-packs')}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-500/20 transition-colors">
                    <Gavel className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold mb-2">Law Packs</h3>
                  <p className="text-sm text-muted-foreground mb-4">Manage compliance law packs</p>
                  <Button variant="outline" size="sm" className="w-full">View Law Packs</Button>
                </CardContent>
              </Card>

              <Card 
                className="glass-panel hover:glass-panel-elevated transition-all duration-300 cursor-pointer group relative"
                onClick={() => navigate('/crystal-templates')}
              >
                <div className="absolute top-2 right-2">
                  <Crown className="h-4 w-4 text-primary" />
                </div>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Crystal HACCP Templates</h3>
                  <p className="text-sm text-muted-foreground mb-4">Professional cleaning templates</p>
                  <Button variant="outline" size="sm" className="w-full">Browse Templates</Button>
                </CardContent>
              </Card>

              <Card 
                className="glass-panel hover:glass-panel-elevated transition-all duration-300 cursor-pointer group"
                onClick={() => navigate('/units')}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-accent/20 transition-colors">
                    <Building2 className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <h3 className="font-semibold mb-2">Manage Units</h3>
                  <p className="text-sm text-muted-foreground mb-4">Configure your locations</p>
                  <Button variant="secondary" size="sm" className="w-full">Manage Units</Button>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <p className="text-sm text-muted-foreground">Latest updates from your food safety operations</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center gap-4 p-3 rounded-lg bg-accent/5">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {activity.action} {activity.entity_type} activity
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="flex items-center gap-4 p-3 rounded-lg bg-accent/5">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">Opening checklist completed</p>
                          <p className="text-xs text-muted-foreground">Kitchen Unit A • 2 hours ago</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 p-3 rounded-lg bg-accent/5">
                        <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">Temperature deviation recorded</p>
                          <p className="text-xs text-muted-foreground">Freezer Unit B • 4 hours ago</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 p-3 rounded-lg bg-accent/5">
                        <Users className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">New staff member added</p>
                          <p className="text-xs text-muted-foreground">Sarah Johnson • 1 day ago</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Dashboard;